import { deserializeError, VivaError } from '@viva/core';
import type { CallContext, Endpoint, RequestMessage, ResponseMessage } from './protocol';

interface Pending {
  resolve(value: unknown): void;
  reject(error: unknown): void;
  onProgress?: (value: unknown) => void;
  cleanup(): void;
}

/** Caller side of `exposeApi`. */
export class RpcClient {
  private nextId = 1;
  private readonly pending = new Map<number, Pending>();

  constructor(private readonly endpoint: Endpoint) {
    endpoint.addEventListener('message', (event: MessageEvent) => this.receive(event.data as ResponseMessage));
  }

  call<T>(method: string, args: unknown[], context: CallContext = {}): Promise<T> {
    if (context.signal?.aborted) return Promise.reject(new VivaError('cancelled', 'Cancelled'));
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      const onAbort = () => this.post({ type: 'cancel', id });
      context.signal?.addEventListener('abort', onAbort);
      this.pending.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        onProgress: context.onProgress as ((value: unknown) => void) | undefined,
        cleanup: () => context.signal?.removeEventListener('abort', onAbort),
      });
      this.post({ type: 'call', id, method, args });
    });
  }

  /** Fails every in-flight call, e.g. when the worker crashed. */
  failAll(error: VivaError): void {
    for (const [id, pending] of this.pending) {
      pending.cleanup();
      pending.reject(error);
      this.pending.delete(id);
    }
  }

  private post(message: RequestMessage): void {
    this.endpoint.postMessage(message);
  }

  private receive(message: ResponseMessage): void {
    const pending = this.pending.get(message.id);
    if (!pending) return;
    if (message.type === 'progress') {
      pending.onProgress?.(message.value);
      return;
    }
    this.pending.delete(message.id);
    pending.cleanup();
    if (message.type === 'result') pending.resolve(message.value);
    else pending.reject(deserializeError(message.error));
  }
}
